import { DateRangeFilter, FilterList, SingleSelectSimpleFilter } from "@dash/filters";
import { Button } from "@dash/form";
import { Page } from "@dash/page";
import { QuerySkeleton, Skeleton } from "@dash/skeleton";
import { keyframes, useTheme } from "@emotion/react";
import { parseISO } from "date-fns";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { MdAdd, MdArrowBack, MdCode, MdDeleteOutline, MdRefresh, MdTerminal } from "react-icons/md";
import { Link, useParams, useSearchParams } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { ReportCell, ReportCellResult } from "@megaron/crm-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";
import { useClientManager } from "@megaron/react-clients";
import { newUuid, Uuid } from "@megaron/uuid";

import { CellEditor, IconButton } from "./CellEditor";

import {
  currentSpan,
  dateRangeLabels,
  periodFromSearchParams,
  periodToSearchParams,
  ReportDateRange,
  reportDateRanges,
  ReportPeriod,
  toIsoDate,
} from "./dateRanges";
import { DownloadReportMenu } from "./DownloadReportMenu";
import { ExportFormat, exportReport } from "./exportReport";
import { CellResult, hasOutput } from "./notebook/CellResult";
import { NotebookView } from "./notebook/NotebookView";
import { RemoveReportDialog } from "./RemoveReportDialog";

const gap = "1rem";
const titleLineHeight = "1.95rem";
const descriptionLineHeight = "1.3125rem";
const chartPlaceholderHeight = "260px";
const kpiPlaceholderHeight = "100px";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const ReportPage: React.FC = () => {
  const theme = useTheme();
  const { reportId } = useParams<{ reportId: string }>();
  const reportQuery = useClientManager("crm")
    .reportQuery()
    .useQuery({ uuid: reportId as Uuid });
  const report = reportQuery.data;
  const [searchParams, setSearchParams] = useSearchParams();
  const period = useMemo(() => periodFromSearchParams(searchParams), [searchParams]);
  const [start, end] = currentSpan(period);
  const outputsQuery = useClientManager("crm")
    .reportOutputsQuery()
    .useQuery(
      { uuid: reportId as Uuid, start, end },
      { refetchOnWindowFocus: false, staleTime: 5 * 60_000, keepPreviousData: true },
    );
  const refresh = useClientManager("crm")
    .reportOutputsQuery()
    .useMutation({ onSuccess: (data, _, { queryClient }) => queryClient.setQueryData(outputsQuery.key, data) });
  const results = useMemo(
    () => new Map(outputsQuery.data?.map((result) => [result.uuid, result])),
    [outputsQuery.data],
  );
  const isRefreshing = outputsQuery.isFetching || refresh.isLoading;
  const isRunning = !report || isRefreshing;
  const content = useRef<HTMLDivElement>(null);
  const canEdit = useContext(IamAuthContext).iamUser?.roles.includes("crm.admin") ?? false;
  const [draft, setDraft] = useState<ReportCell[] | null>(null);
  const [editingIds, setEditingIds] = useState<Uuid[]>([]);
  const [savingId, setSavingId] = useState<Uuid | null>(null);
  const [isNotebook, setIsNotebook] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const save = useClientManager("crm")
    .saveReport()
    .useMutation({
      onSuccess: async (_, saved, { queryClient }) => {
        await Promise.all([
          queryClient.invalidateQueries(reportQuery.key),
          queryClient.invalidateQueries(["crm", "reportOutputsQuery"]),
        ]);
        setEditingIds((ids) => {
          const stillEditing = ids.filter((id) => id !== savingId);
          setDraft((cells) => (isNotebook || stillEditing.length > 0 ? (cells ?? saved.cells) : null));
          return stillEditing;
        });
        setSavingId(null);
      },
      onError: () => setSavingId(null),
    });

  const updateCell = (index: number, cell: ReportCell) =>
    setDraft((cells) => cells && cells.map((c, i) => (i === index ? cell : c)));
  const moveCell = (index: number, offset: -1 | 1) =>
    setDraft((cells) => {
      if (!cells) return cells;
      const next = [...cells];
      next.splice(index + offset, 0, ...next.splice(index, 1));
      return next;
    });
  const removeCell = (uuid: Uuid) => {
    if (!report) return;
    setDraft((cells) => cells && cells.filter((cell) => cell.uuid !== uuid));
    setSavingId(uuid);
    save.mutate({ ...report, cells: report.cells.filter((cell) => cell.uuid !== uuid) });
  };
  const editCell = (uuid: Uuid) => {
    if (!draft && report) setDraft(report.cells);
    setEditingIds((ids) => [...ids, uuid]);
  };
  const addCell = () => {
    if (!report) return;
    const uuid = newUuid();
    setDraft((cells) => [...(cells ?? report.cells), { uuid, title: "", width: "full", visible: true, code: "" }]);
    setEditingIds((ids) => [...ids, uuid]);
  };
  const saveCell = (uuid: Uuid) => {
    if (!report || !draft) return;
    setSavingId(uuid);
    save.mutate({ ...report, cells: draft });
  };
  const cancelCell = (uuid: Uuid) => {
    if (!report) return;
    const stillEditing = editingIds.filter((id) => id !== uuid);
    const saved = report.cells.find((cell) => cell.uuid === uuid);
    setEditingIds(stillEditing);
    setDraft((cells) =>
      stillEditing.length === 0 || !cells
        ? null
        : saved
          ? cells.map((cell) => (cell.uuid === uuid ? saved : cell))
          : cells.filter((cell) => cell.uuid !== uuid),
    );
  };
  const cancelEdit = () => {
    setDraft(null);
    setEditingIds([]);
  };
  const openNotebook = () => {
    if (!report) return;
    setDraft(report.cells);
    setEditingIds([]);
    setIsNotebook(true);
  };
  const closeNotebook = () => {
    setIsNotebook(false);
    cancelEdit();
  };
  const runNotebook = () => {
    if (!report || !draft) return;
    if (JSON.stringify(draft) === JSON.stringify(report.cells))
      return refresh.mutate({ uuid: reportId as Uuid, start, end, refresh: true });
    save.mutate({ ...report, cells: draft });
  };

  useEffect(() => {
    if (report) document.title = report.title;
  }, [report?.title]);

  const download = (format: ExportFormat) => {
    if (!report || !content.current) return;
    exportReport(
      format,
      {
        title: report.title,
        description: report.description,
        period: { start, end },
        cells: report.cells.map((cell) => ({
          uuid: cell.uuid,
          title: cell.title,
          code: cell.code,
          result: results.get(cell.uuid) ?? null,
        })),
      },
      content.current,
    );
  };

  const changePeriod = (next: ReportPeriod) => setSearchParams(periodToSearchParams(next), { replace: true });

  const selectDateRange = (dateRange: ReportDateRange) =>
    changePeriod({
      dateRange,
      customRange: dateRange === "custom" ? (period.customRange ?? { start, end }) : null,
    });

  return (
    <Page css={{ maxWidth: "1680px", paddingBottom: "2rem" }}>
      <div css={{ display: "flex", flexDirection: "column", paddingTop: "1rem" }}>
        <Link
          to={getMegaraxPath.uri("megarax://crm/reports")}
          css={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.25rem",
            fontSize: "0.8125rem",
            color: theme.colors.secondaryText,
            alignSelf: "flex-start",
          }}
        >
          <MdArrowBack /> Raporty
        </Link>

        <div css={{ display: "flex", alignItems: "flex-start", gap: "1rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
          <div css={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {report ? (
              <h1
                css={{
                  margin: 0,
                  fontFamily: theme.displayFontFamily,
                  fontSize: "1.625rem",
                  fontWeight: 700,
                  color: theme.colors.primary,
                  lineHeight: titleLineHeight,
                }}
              >
                {report.title}
              </h1>
            ) : (
              <TextLine height={titleLineHeight} width="min(360px, 70%)" />
            )}
            {report ? (
              <p
                css={{
                  margin: 0,
                  color: theme.colors.secondaryText,
                  fontSize: "0.9375rem",
                  lineHeight: descriptionLineHeight,
                }}
              >
                {report.description}
              </p>
            ) : (
              <TextLine height={descriptionLineHeight} width="min(560px, 90%)" />
            )}
            {(outputsQuery.isError || refresh.isError) && (
              <span css={{ fontSize: "0.75rem", color: theme.colors.danger }}>Nie udało się przeliczyć raportu</span>
            )}
          </div>
          <Button
            variant="outline"
            icon={<MdRefresh />}
            onClick={() => refresh.mutate({ uuid: reportId as Uuid, start, end, refresh: true })}
            isDisabled={isRunning}
            isLoading={isRefreshing}
          >
            {isRefreshing ? "Przeliczanie…" : "Odśwież"}
          </Button>
          {canEdit && !isNotebook && (
            <Button variant="outline" icon={<MdTerminal />} onClick={openNotebook} isDisabled={!report}>
              Edytuj notebook
            </Button>
          )}
          <DownloadReportMenu onDownload={download} isDisabled={isRunning} />
          {canEdit && (
            <Button
              variant="outline"
              icon={<MdDeleteOutline />}
              onClick={() => setIsRemoving(true)}
              isDisabled={!report}
              css={{ color: theme.colors.danger, borderColor: theme.colors.danger }}
            >
              Usuń
            </Button>
          )}
        </div>
        {save.isError && (
          <span css={{ fontSize: "0.75rem", color: theme.colors.danger }}>Nie udało się zapisać raportu</span>
        )}

        <FilterList>
          <SingleSelectSimpleFilter<ReportDateRange>
            label="Okres"
            value={period.dateRange}
            options={reportDateRanges.map((r) => ({ value: r, label: dateRangeLabels[r] }))}
            onChange={(value) => value && selectDateRange(value)}
            isUnselectDisabled
          />
          {period.dateRange === "custom" && (
            <DateRangeFilter
              label="Wybierz daty"
              startDate={parseISO(start)}
              endDate={parseISO(end)}
              onChange={({ startDate, endDate }) => {
                if (!startDate && !endDate) return selectDateRange("last30Days");
                changePeriod({
                  dateRange: "custom",
                  customRange: {
                    start: toIsoDate(startDate ?? parseISO(start)),
                    end: toIsoDate(endDate ?? parseISO(end)),
                  },
                });
              }}
            />
          )}
        </FilterList>

        <div ref={content}>
          <QuerySkeleton query={reportQuery} customSekelton={<CellGridSkeleton />}>
            {(report) => {
              const cells = draft ?? report.cells;
              if (isNotebook)
                return (
                  <NotebookView
                    cells={cells}
                    savedCells={report.cells}
                    results={results}
                    isRunning={isRefreshing}
                    isSaving={save.isLoading}
                    onChange={setDraft}
                    onRun={runNotebook}
                    onClose={closeNotebook}
                  />
                );
              return (
                <CellGrid>
                  {groupCodeOnly(cells, (cell) => !cell.visible && !(draft && editingIds.includes(cell.uuid)))
                    .filter((group) => canEdit || group.kind === "cell")
                    .map((group) =>
                      group.kind === "code" ? (
                        <CodeStrip key={group.cells[0].uuid} cells={group.cells} onEdit={editCell} />
                      ) : draft && editingIds.includes(group.cell.uuid) ? (
                        <CellFrame key={group.cell.uuid} width="full">
                          <CellEditor
                            cell={group.cell}
                            isFirst={group.index === 0}
                            isLast={group.index === cells.length - 1}
                            isSaving={savingId === group.cell.uuid}
                            onChange={(next) => updateCell(group.index, next)}
                            onMove={(offset) => moveCell(group.index, offset)}
                            onRemove={() => removeCell(group.cell.uuid)}
                            onCancel={() => cancelCell(group.cell.uuid)}
                            onSave={() => saveCell(group.cell.uuid)}
                          >
                            {results.has(group.cell.uuid) && (
                              <CellResult result={results.get(group.cell.uuid) as ReportCellResult} />
                            )}
                          </CellEditor>
                        </CellFrame>
                      ) : (
                        <CellView
                          key={group.cell.uuid}
                          cell={group.cell}
                          result={results.get(group.cell.uuid)}
                          isRunning={isRunning}
                          onEdit={canEdit ? () => editCell(group.cell.uuid) : undefined}
                        />
                      ),
                    )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={addCell}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.375rem",
                        padding: "0.875rem",
                        border: `1px dashed ${theme.colors.border}`,
                        borderRadius: theme.mediumBorderRadius,
                        background: "transparent",
                        color: theme.colors.secondaryText,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        "&:hover": {
                          color: theme.colors.primary,
                          borderColor: theme.colors.primary,
                          background: theme.colors.primaryLight,
                        },
                      }}
                    >
                      <MdAdd /> Dodaj blok
                    </button>
                  )}
                </CellGrid>
              );
            }}
          </QuerySkeleton>
        </div>
      </div>

      {isRemoving && report && <RemoveReportDialog report={report} onClose={() => setIsRemoving(false)} />}
    </Page>
  );
};

const CellGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();

  return (
    <div
      css={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap,
        paddingBottom: "3rem",
        "& > *": { flex: "0 0 100%", minWidth: 0 },
        "& > [data-width='half']": { flex: `0 0 calc((100% - ${gap}) / 2)` },
        "& > [data-width='third']": { flex: `0 0 calc((100% - 2 * ${gap}) / 3)` },
        "& > [data-width='quarter']": { flex: `0 0 calc((100% - 3 * ${gap}) / 4)` },
        [`@media (max-width: ${theme.desktopMinWidth})`]: {
          "& > [data-width='third'], & > [data-width='quarter']": { flex: `0 0 calc((100% - ${gap}) / 2)` },
        },
        [`@media (max-width: ${theme.tabletMinWidth})`]: {
          "& > [data-width='half'], & > [data-width='third']": { flex: "0 0 100%" },
        },
      }}
    >
      {children}
    </div>
  );
};

const CellGridSkeleton: React.FC = () => (
  <CellGrid>
    <CellFrame width="full" title={null}>
      <BodySkeleton height={kpiPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="full" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="half" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
    <CellFrame width="half" title={null}>
      <BodySkeleton height={chartPlaceholderHeight} />
    </CellFrame>
  </CellGrid>
);

const CellView: React.FC<{
  cell: ReportCell;
  result: ReportCellResult | undefined;
  isRunning: boolean;
  onEdit?: () => void;
}> = ({ cell, result, isRunning, onEdit }) => {
  return (
    <CellFrame
      width={cell.width}
      title={cell.title}
      action={
        onEdit && (
          <IconButton title="Edytuj kod" onClick={onEdit} css={{ margin: "-0.375rem -0.5rem -0.375rem 0" }}>
            <MdCode />
          </IconButton>
        )
      }
    >
      {isRunning && !result ? (
        <BodySkeleton height={chartPlaceholderHeight} />
      ) : (
        <div
          css={{
            opacity: isRunning ? 0.5 : 1,
            transition: "opacity 120ms",
            animation: `${fadeIn} 200ms ease-out`,
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {result && <CellResult result={result} />}
        </div>
      )}
    </CellFrame>
  );
};

const CellFrame: React.FC<{
  width: ReportCell["width"];
  title?: string | null;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ width, title, action, children }) => {
  const theme = useTheme();

  return (
    <section
      data-width={width}
      css={{
        background: "white",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        borderRadius: theme.mediumBorderRadius,
        padding: "1rem 1.25rem 1.25rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
      }}
    >
      {title === null ? (
        <TextLine height="1.25rem" width="180px" />
      ) : (
        (title || action) && (
          <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <h3
              css={{
                flex: 1,
                margin: 0,
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: "#1d1c1a",
                lineHeight: "1.25rem",
              }}
            >
              {title}
            </h3>
            {action}
          </div>
        )
      )}
      {children}
    </section>
  );
};

const BodySkeleton: React.FC<{ height: string }> = ({ height }) => {
  const theme = useTheme();

  return <Skeleton height={height} css={{ display: "block", borderRadius: theme.smallBorderRadius }} />;
};

const TextLine: React.FC<{ height: string; width: string }> = ({ height, width }) => (
  <span css={{ height, display: "flex", alignItems: "center" }}>
    <Skeleton width={width} height="60%" css={{ borderRadius: "4px" }} />
  </span>
);

type CellGroup = { kind: "cell"; cell: ReportCell; index: number } | { kind: "code"; cells: ReportCell[] };

const groupCodeOnly = (cells: ReportCell[], isCodeOnly: (cell: ReportCell) => boolean) =>
  cells.reduce<CellGroup[]>((groups, cell, index) => {
    const last = groups[groups.length - 1];
    if (!isCodeOnly(cell)) return [...groups, { kind: "cell", cell, index }];
    if (last?.kind === "code") return [...groups.slice(0, -1), { kind: "code", cells: [...last.cells, cell] }];
    return [...groups, { kind: "code", cells: [cell] }];
  }, []);

const CodeStrip: React.FC<{ cells: ReportCell[]; onEdit: (uuid: Uuid) => void }> = ({ cells, onEdit }) => (
  <div data-width="full" css={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
    {cells.map((cell) => (
      <CodeChip key={cell.uuid} cell={cell} onEdit={() => onEdit(cell.uuid)} />
    ))}
  </div>
);

const codeLabel = (code: string) => {
  const lines = code.split("\n").map((line) => line.trim());
  const definition = lines.find((line) => /^(def|class) /.test(line));
  if (definition)
    return definition
      .replace(/^(def|class) /, "")
      .replace(/\(.*$/, "")
      .replace(/:$/, "");
  return lines.find((line) => line && !line.startsWith("#")) ?? "pusta komórka";
};

const CodeChip: React.FC<{ cell: ReportCell; onEdit: () => void }> = ({ cell, onEdit }) => {
  const theme = useTheme();

  return (
    <button
      type="button"
      title={cell.code}
      onClick={onEdit}
      css={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        maxWidth: "260px",
        padding: "0.25rem 0.625rem",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: "999px",
        background: "white",
        color: theme.colors.secondaryText,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "0.75rem",
        cursor: "pointer",
        "&:hover": { background: theme.colors.primaryLight, color: theme.colors.primary },
      }}
    >
      <MdCode css={{ flexShrink: 0 }} />
      <span css={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{codeLabel(cell.code)}</span>
    </button>
  );
};
